import { logger } from '@luna/logger';

/**
 * Copies the given text to the clipboard.
 *
 * This function attempts to use the modern Clipboard API first. If that fails
 * (e.g., due to browser support or permissions), it falls back to a method
 * using document.execCommand('copy').
 *
 * @param {string} textToCopy - The text to be copied to the clipboard.
 * @returns {Promise<void>} A promise that resolves when the copy operation is complete.
 *
 * @throws {Error} If both copy methods fail. The error is logged but not thrown to the caller.
 *
 * @example
 * // Basic usage
 * try {
 *   await copyText('Hello, clipboard!');
 *   console.log('Text copied successfully!');
 * } catch (error) {
 *   console.error('Failed to copy text:', error);
 * }
 *
 * @example
 * // Usage in a React component
 * function CopyButton({ textToCopy }) {
 *   const [isCopied, setIsCopied] = useState(false);
 *
 *   const handleCopy = async () => {
 *     try {
 *       await copyText(textToCopy);
 *       setIsCopied(true);
 *       setTimeout(() => setIsCopied(false), 2000);
 *     } catch (error) {
 *       console.error('Failed to copy:', error);
 *     }
 *   };
 *
 *   return (
 *     <button onClick={handleCopy}>
 *       {isCopied ? 'Copied!' : 'Copy to Clipboard'}
 *     </button>
 *   );
 * }
 */
export const copyText = async (textToCopy: string): Promise<void> => {
    try {
        await navigator.clipboard.writeText(textToCopy);
    } catch (e) {
        logger.debug(e);
        const el = document.createElement('textarea');
        el.value = textToCopy;
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
    }
};
